/** Colores de agenda: por agente (vista «todos») o por tipo de cita (un agente / móvil). */

export const COLORES_AGENTE_AGENDA = {
  "26c8190c-4017-408f-b2ee-1b9962d31f1e": "#F6EF34",
  "3d4575ed-df3d-491d-81a4-bdabf70e5416": "#DB2777",
  "d875e087-eeb1-4f36-91f3-569a7c1f4ad2": "#EA580C",
  "91e3ed12-b4ab-41bd-acc8-eb26f9977bf0": "#16A34A",
  "66b5f793-803d-4371-9d2b-eb8fce55f297": "#2563EB",
  "b54358a8-388c-48ff-8fdd-1158fd88f073": "#64748B",
};

/** Modo claro / móvil: pasteles opacos. */
export const COLORES_TIPO_CITA_AGENDA = {
  visita:    { fondo: "#BFDBFE", borde: "#2563EB", etiqueta: "Visita" },
  llamada:   { fondo: "#BBF7D0", borde: "#16A34A", etiqueta: "Llamada" },
  reunion:   { fondo: "#DDD6FE", borde: "#7C3AED", etiqueta: "Reunión" },
  captacion: { fondo: "#FDE68A", borde: "#D97706", etiqueta: "Captación" },
  valoracion: { fondo: "#FBCFE8", borde: "#DB2777", etiqueta: "Valoración" },
  otro:      { fondo: "#E2E8F0", borde: "#64748B", etiqueta: "Otro" },
};

/** Modo oscuro: tonos más vivos (como la leyenda antigua por agente). */
export const COLORES_TIPO_CITA_AGENDA_DARK = {
  visita:    { fondo: "#60A5FA", borde: "#1D4ED8", etiqueta: "Visita" },
  llamada:   { fondo: "#4ADE80", borde: "#15803D", etiqueta: "Llamada" },
  reunion:   { fondo: "#E879F9", borde: "#9333EA", etiqueta: "Reunión" },
  captacion: { fondo: "#FACC15", borde: "#CA8A04", etiqueta: "Captación" },
  valoracion: { fondo: "#F472B6", borde: "#BE185D", etiqueta: "Valoración" },
  otro:      { fondo: "#94A3B8", borde: "#475569", etiqueta: "Otro" },
};

/** Texto de citas en calendario: siempre negro. */
export const AGENDA_TEXTO_CITA = "#000000";

export function getColoresTipoCitaAgenda(isDark = false) {
  return isDark ? COLORES_TIPO_CITA_AGENDA_DARK : COLORES_TIPO_CITA_AGENDA;
}

/** Mezcla dos hex #RRGGBB; ratio = peso de `conHex` (opaco). */
export function mezclarColorAgenda(hex, conHex, ratio) {
  const parse = (h) => {
    let s = String(h || "#888888").trim();
    if (s.startsWith("#")) s = s.slice(1);
    if (s.length === 3) s = [...s].map((ch) => ch + ch).join("");
    if (s.length !== 6) return [0x88, 0x88, 0x88];
    return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16));
  };
  const [r1, g1, b1] = parse(hex);
  const [r2, g2, b2] = parse(conHex);
  const mix = (a, b) => Math.min(255, Math.max(0, Math.round(a * (1 - ratio) + b * ratio)));
  const r = mix(r1, r2);
  const g = mix(g1, g2);
  const b = mix(b1, b2);
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

function hexToRgb01(hex) {
  let s = String(hex || "#888888").trim();
  if (s.startsWith("#")) s = s.slice(1);
  if (s.length === 3) s = [...s].map((ch) => ch + ch).join("");
  if (s.length !== 6) return [0.53, 0.53, 0.53];
  return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16) / 255);
}

function relativeLuminanceHex(hex) {
  const [r, g, b] = hexToRgb01(hex).map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Texto legible sobre el color del agente (p. ej. amarillo AF → negro). */
export function textoSobreColorHex(hex) {
  return relativeLuminanceHex(hex) > 0.45 ? "#000000" : "#ffffff";
}

export function textoSobreColorAgente(agenteId, isDark = false) {
  const borde = getColorAgenteAgenda(agenteId);
  const fondo = fondoAgenteAgendaPorModo(borde, isDark);
  return textoSobreColorHex(fondo);
}

export function getColorAgenteAgenda(agenteId) {
  const id = String(agenteId ?? "").trim().toLowerCase();
  if (!id) return "#94A3B8";
  const hit = Object.entries(COLORES_AGENTE_AGENDA).find(([k]) => k.toLowerCase() === id);
  return hit ? hit[1] : "#94A3B8";
}

const COLORES_USUARIO_FALLBACK = [
  "#2563EB", "#DB2777", "#EA580C", "#16A34A", "#7C3AED", "#0891B2", "#CA8A04",
];

function hashColorUsuario(id) {
  const s = String(id ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
  return COLORES_USUARIO_FALLBACK[Math.abs(h) % COLORES_USUARIO_FALLBACK.length];
}

/** Color de leyenda / cita en vista «todos» según usuario (vía agente vinculado o hash). */
export function getColorUsuarioAgenda(usuario) {
  if (usuario?.agente_id) return getColorAgenteAgenda(usuario.agente_id);
  if (usuario?.id != null) return hashColorUsuario(usuario.id);
  return "#94A3B8";
}

export function getColorCitaVistaTodos(cita, { usuarioPorId = {}, agenteAUsuario = {} } = {}) {
  const uid =
    cita?.usuario_id != null
      ? String(cita.usuario_id)
      : (cita?.agente_id && agenteAUsuario[String(cita.agente_id)]);
  if (uid) {
    const u = usuarioPorId[uid];
    return getColorUsuarioAgenda(u || { id: uid, agente_id: cita?.agente_id });
  }
  if (cita?.agente_id) return getColorAgenteAgenda(cita.agente_id);
  return "#94A3B8";
}

/** Fondo por agente: en oscuro color vivo (como leyenda); en claro pastel suave. */
export function fondoAgenteAgendaPorModo(agentColor, isDark) {
  if (isDark) return agentColor;
  return mezclarColorAgenda(agentColor, "#ffffff", 0.5);
}

export function getEstiloCitaAgenda(cita, verTodos, isDark = false, colorAsignacion) {
  if (verTodos) {
    const borde = colorAsignacion
      ? colorAsignacion(cita)
      : getColorCitaVistaTodos(cita);
    const fondo = fondoAgenteAgendaPorModo(borde, isDark);
    return { borde, fondo, color: AGENDA_TEXTO_CITA, colorSec: AGENDA_TEXTO_CITA };
  }
  const tipo = String(cita?.tipo || "otro").toLowerCase();
  const paleta = getColoresTipoCitaAgenda(isDark);
  const t = paleta[tipo] || paleta.otro;
  return {
    borde: t.borde,
    fondo: t.fondo,
    color: AGENDA_TEXTO_CITA,
    colorSec: AGENDA_TEXTO_CITA,
  };
}

export function getEstiloNdAgenda(agenteId, isDark = false) {
  const borde = getColorAgenteAgenda(agenteId);
  const fondo = fondoAgenteAgendaPorModo(borde, isDark);
  return { borde, fondo, color: textoSobreColorHex(fondo) };
}

export function propsCitaPorTipo(cita, { isDark = false, arrastre = false } = {}) {
  const tipo = String(cita?.tipo || "otro").toLowerCase();
  const paleta = getColoresTipoCitaAgenda(isDark);
  const t = paleta[tipo] || paleta.otro;
  return {
    className: "agenda-cita-tipo",
    "data-tipo": tipo,
    style: {
      background: t.fondo,
      backgroundColor: t.fondo,
      borderLeft: `4px solid ${t.borde}`,
      opacity: arrastre ? 0.45 : 1,
    },
  };
}

export function propsLeyendaTipo(tipo, isDark = false) {
  const paleta = getColoresTipoCitaAgenda(isDark);
  const t = paleta[tipo] || paleta.otro;
  return {
    className: "agenda-leyenda-tipo",
    "data-tipo": tipo,
    style: {
      background: t.fondo,
      backgroundColor: t.fondo,
    },
  };
}

/** Estilos de cita: por tipo (un agente) o por agente (vista «todos»). */
export function estilosCajaCita(estilo, cita, { verTodosAgentes = false, isDark = false, arrastre = false } = {}) {
  if (!verTodosAgentes && cita) {
    return propsCitaPorTipo(cita, { isDark, arrastre });
  }
  const { borde, fondo } = estilo;
  return {
    backgroundColor: fondo,
    color: AGENDA_TEXTO_CITA,
    borderLeft: `3px solid ${borde}`,
    borderTop: `1px solid ${mezclarColorAgenda(borde, "#0f172a", 0.22)}`,
    borderRight: `1px solid ${mezclarColorAgenda(borde, "#0f172a", 0.22)}`,
    borderBottom: `1px solid ${mezclarColorAgenda(borde, "#0f172a", 0.22)}`,
    borderRadius: 4,
    boxSizing: "border-box",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    opacity: arrastre ? 0.45 : 1,
  };
}
